import { useEffect } from 'react';
import { useForm, type FieldValues, type DefaultValues } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

/**
 * Wraps useForm with sessionStorage persistence.
 *
 * On mount: uses defaultValues as-is (caller is responsible for merging
 * sessionStorage before passing — see useCompanyFormLogic for the pattern).
 * On every value change: serialises current form values to sessionStorage.
 *
 * Returns the full useForm return object plus a clearStorage() helper that
 * the caller should invoke when the form is successfully submitted.
 */
export function usePersistentForm<T extends FieldValues>(
  storageKey: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schema: any,
  defaultValues: DefaultValues<T>
) {
  const form = useForm<T>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues,
  });

  useEffect(() => {
    const subscription = form.watch((values) => {
      sessionStorage.setItem(storageKey, JSON.stringify(values));
    });
    return () => subscription.unsubscribe();
  }, [form, storageKey]);

  const clearStorage = () => sessionStorage.removeItem(storageKey);

  return { ...form, clearStorage };
}
